import React, { useMemo } from 'react';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { useStepHook } from '../logic/useStepHook';
import { ToolTipSectionView } from './ToolTipSectionView';
import { renderMap, StepViewComponentsProps } from './StepViewComponents';
import { ChangePhoneFlow } from '../ChangePhoneSteps';

export interface StepViewProps {
  title?: string;
  handleClick: (value: string) => void;
  headLineSmallText: string;
  buttonTitle: string;
  handleResendMail?: () => void;
  isLoading?: boolean;
  numOfDigits: number;
  svg?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  countdown?: number;
  children?: React.ReactNode;
}

export const StepView = ({
  title,
  handleClick,
  headLineSmallText,
  buttonTitle,
  numOfDigits,
  handleResendMail,
  isLoading,
  svg,
  countdown,
  children,
}: StepViewProps) => {
  const {
    t,
    nationalIdDescription,
    step,
    dispatch,
    data: { error },
    inputData,
    handleOnInputChange,
    isPhoneValid,
  } = useStepHook();
  const resendDisabled = countdown !== 0;
  const stepViewComponentsProps: StepViewComponentsProps = {
    numOfDigits,
    handleOnInputChange,
    nationalIdDescription,
    error,
    inputData,
    step,
    dispatch,
  };
  const buttonPadding = handleResendMail ? 'pt-[35px]' : 'pt-[56px]';
  const inputForm = useMemo(() => renderMap(stepViewComponentsProps), [step, stepViewComponentsProps]);
  const filteredInputData = inputData.replace(/[^a-zA-Z0-9]/g, '');
  const disabled =
    (step !== ChangePhoneFlow.PHONE_INPUT ? filteredInputData.length < 4 : !isPhoneValid) || Boolean(error);

  return (
    <div className='flex flex-col items-center justify-center w-[400px]'>
      <div className='flex flex-col mb-[24px]'>
        <div className='flex items-center justify-center'>
          <p className='text-bw08 text-base font-metricRMedium w-full text-center '>{title ?? children}</p>
          {svg && <ToolTipSectionView svgComponent={svg} />}
        </div>

        <div className='text-bw07 text-sm font-metricR  text-center' id='subheader'>
          {headLineSmallText.split('\n').map((line) => (
            <p key={line} className={line === headLineSmallText.split('\n')[0] ? '' : 'mt-1'}>
              {line}
            </p>
          ))}
        </div>
      </div>
      {inputForm}
      <div className='h-[40px]'>
        {handleResendMail && (
          <button
            type='button'
            disabled={resendDisabled}
            onClick={handleResendMail}
            id='mail-resend'
            className='btn-link'
          >
            {resendDisabled && `${t('CHANGE_PHONE.MAIL.RESEND_TIMER')}: ${countdown}`}
            {!resendDisabled && t('CHANGE_PHONE.MAIL.RESEND')}
          </button>
        )}
      </div>
      <div className={buttonPadding}>
        <LiliButton
          id='phone-submit'
          title={buttonTitle}
          disabled={disabled}
          isLoading={isLoading}
          onClick={() => handleClick(inputData)}
          type={LiliButtonType.PRIMARY}
          className='w-[440px] h-[44px]'
        />
      </div>
    </div>
  );
};
